// src/ProductsHandler.js
import shopifyFetch from "./APIFetch";

/* ──────────────────────────────────────────────────────────
   Shared helpers for inventory-scope fallback
   ────────────────────────────────────────────────────────── */
const VARIANT_FIELDS_WITH_QTY = `
  id
  title
  price { amount currencyCode }
  compareAtPrice { amount currencyCode }
  selectedOptions { name value }
  quantityAvailable
  availableForSale
  currentlyNotInStock
`;

const VARIANT_FIELDS_NO_QTY = `
  id
  title
  price { amount currencyCode }
  compareAtPrice { amount currencyCode }
  selectedOptions { name value }
  availableForSale
  currentlyNotInStock
`;

const PRODUCT_CORE_FIELDS = (withQty) => `
  id
  handle
  title
  description
  descriptionHtml
  images(first: 100) { edges { node { url altText } } }
  variants(first: 100) {
    edges {
      node {
        ${(withQty ? VARIANT_FIELDS_WITH_QTY : VARIANT_FIELDS_NO_QTY).trim()}
      }
    }
  }
`;

const slugifyFallback = (value) => {
    if (!value) return "";
    return String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ł/g, "l")
        .replace(/Ł/g, "l")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
};

const lacksInventoryScope = (err) => {
    try {
        const s = JSON.stringify(err?.errors ?? err) || String(err?.message ?? err);
        return s.includes("quantityAvailable") && s.includes("ACCESS_DENIED");
    } catch {
        return false;
    }
};

/* ============================================================
 * LISTING: loadProducts (filters + local sorting + ?nazwa + main/subcategory)
 * ============================================================ */
export const loadProducts = async (
    sortType,
    kategorieArr,
    markiArr,
    isBestseller,
    isPromocja,
    isNewCollection,
    priceRange,
    nameQuery,
    mainCategory,
    subCategory,
    after = null
) => {
    try {
        /* ---------------- helpers ---------------- */
        const esc = (s) => String(s ?? "").trim().replace(/'/g, "\\'");
        const escDQ = (s) => String(s ?? "").replace(/(["\\])/g, "\\$1");
        const escWild = (s) => String(s ?? "").replace(/(["\\])/g, "\\$1");

        const decodeEntities = (s) => {
            if (s == null) return "";
            try {
                const doc = new DOMParser().parseFromString(String(s), "text/html");
                return doc.documentElement.textContent || "";
            } catch {
                return String(s)
                    .replace(/&nbsp;/g, " ")
                    .replace(/\u00A0/g, " ")
                    .replace(/&amp;/g, "&")
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;|&apos;/g, "'")
                    .replace(/&lt;/g, "<")
                    .replace(/&gt;/g, ">");
            }
        };

        const stripAcc = (s = "") =>
            s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ł/g, "l").replace(/Ł/g, "l");

        const canon = (s) =>
            stripAcc(String(s ?? ""))
                .replace(/\+/g, " ")
                .replace(/\s+/g, " ")
                .trim()
                .toLowerCase();

        const normalizeSpaces = (s) =>
            String(s ?? "")
                .replace(/\s+/g, " ")
                .trim();

        const equalText = (a, b) => normalizeSpaces(a) === normalizeSpaces(b);

        const toList = (raw) => {
            if (!raw) return [];
            let txt = String(raw).replace(/\s+/g, " ").trim();

            try {
                const parsed = JSON.parse(txt);
                if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
            } catch {}

            if (/[,;/|]/.test(txt)) {
                return txt.split(/[,;/|]/g).map((x) => x.trim()).filter(Boolean);
            }
            return [txt];
        };

        const truthy = (v) => {
            const t = String(v ?? "").trim().toLowerCase();
            return t === "true" || t === "1" || t === "yes" || t === "tak";
        };

        const normalizeRange = (rng) => {
            if (!Array.isArray(rng)) return null;
            let [min, max] = rng;
            min = Number(min);
            max = Number(max);
            if (!Number.isFinite(min)) min = null;
            if (!Number.isFinite(max)) max = null;
            if (min == null && max == null) return null;
            if (min != null && max != null && max < min) [min, max] = [max, min];
            return { min, max };
        };

        /* ---------------- brand matching ---------------- */
        const BRAND_STOPWORDS = new Set(["brand", "pro", "shop", "co", "professional", "company"]);

        const tokenize = (s) =>
            canon(s)
                .split(/[.\-_/| ]+/g)
                .filter(Boolean);

        const cleanTokens = (tokens) => tokens.filter((t) => t && !BRAND_STOPWORDS.has(t));

        const brandKeyset = (s) => {
            const full = canon(s);
            const compact = full.replace(/[\s.\-_/|]+/g, "");
            const tokens = cleanTokens(tokenize(s));
            return { full, compact, tokens };
        };

        const wantBrandKeys = (markiArr || []).map(brandKeyset);

        const kCanonList = (kategorieArr || []).map(canon).filter(Boolean);

        const nameNeedle = (nameQuery || "").trim();

        /* ---------------- SHOPIFY SORT ---------------- */
        let sfSortKey = "TITLE";
        let sfReverse = false;
        switch (sortType) {
            case "name-desc":
                sfSortKey = "TITLE";
                sfReverse = false;
                break;
            case "name-asc":
                sfSortKey = "TITLE";
                sfReverse = true;
                break;
            case "price-asc":
                sfSortKey = "PRICE";
                sfReverse = false;
                break;
            case "price-desc":
                sfSortKey = "PRICE";
                sfReverse = true;
                break;
        }

        /* ---------------- BUILD SHOPIFY QUERY ---------------- */
        const qParts = [];

        if (normalizeSpaces(mainCategory))
            qParts.push(`metafields.custom.maincategory:"${escDQ(mainCategory)}"`);

        if (normalizeSpaces(subCategory))
            qParts.push(`metafields.custom.subcategory:"${escDQ(subCategory)}"`);

        if (kCanonList.length) {
            const parts = kategorieArr.map((k) => `metafields.custom.kategoria:"${escDQ(k)}"`);
            qParts.push(parts.length > 1 ? `(${parts.join(" OR ")})` : parts[0]);
        }

        if (isBestseller === true) qParts.push(`metafields.custom.bestseller:true`);
        if (isNewCollection === true) qParts.push(`metafields.custom.newcollection:true`);

        const rng = normalizeRange(priceRange);
        if (rng) {
            if (rng.min != null) qParts.push(`price:>=${rng.min}`);
            if (rng.max != null) qParts.push(`price:<=${rng.max}`);
        }

        if (nameNeedle) {
            const token = stripAcc(nameNeedle).toLowerCase();
            qParts.push(`title:*${escWild(token)}*`);
        }



        const finalQuery = qParts.length ? qParts.join(" AND ") : undefined;
        /* ---------------- SHOPIFY PAGE FETCH ---------------- */
        const queryDoc = `
          query GetProducts($first: Int!, $sortKey: ProductSortKeys!, $reverse: Boolean!, $query: String, $after: String) {
            products(first: $first, sortKey: $sortKey, reverse: $reverse, query: $query, after: $after) {
              edges {
                node {
                  id
                  handle
                  title
                  vendor
                  images(first: 1) { edges { node { url altText } } }
                  variants(first: 100) {
                    edges { node {
                      id
                      title
                      selectedOptions { name value }
                      price { amount currencyCode }
                      compareAtPrice { amount currencyCode }
                      availableForSale
                      currentlyNotInStock
                    }}
                  }
                  kategoriaCustom: metafield(namespace:"custom",key:"kategoria"){ value }
                  markaCustom: metafield(namespace:"custom",key:"marka"){ value }
                  bestsellerCustom: metafield(namespace:"custom",key:"bestseller"){ value }
                  newcollectionCustom: metafield(namespace:"custom",key:"newcollection"){ value }
                  maincategoryCustom: metafield(namespace:"custom",key:"maincategory"){ value }
                  subcategoryCustom: metafield(namespace:"custom",key:"subcategory"){ value }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        `;

        const data = await shopifyFetch(queryDoc, {
            first: 200,
            sortKey: sfSortKey,
            reverse: sfReverse,
            query: finalQuery,
            after: after,
        });



        const edges = data?.products?.edges ?? [];

        /* ---------------- MAP PRODUCTS ---------------- */
        const mapped = edges.map(({ node }) => {
            const vEdges = node.variants?.edges || [];

            let lowest = Infinity;
            let lowestCompare = null;
            let currency = "PLN";
            let hasDiscount = false;

            for (const { node: v } of vEdges) {
                const p = Number(v.price?.amount ?? Infinity);
                const cp = Number(v.compareAtPrice?.amount ?? null);
                if (cp && cp > p) hasDiscount = true;
                if (p < lowest) {
                    lowest = p;
                    currency = v.price?.currencyCode ?? "PLN";
                    lowestCompare = cp;
                }
            }
            if (!Number.isFinite(lowest)) lowest = 0;

            const catTokens = toList(node.kategoriaCustom?.value);
            const markaTokens = toList(node.markaCustom?.value);

            const mainTokens = toList(node.maincategoryCustom?.value);
            const subTokens = toList(node.subcategoryCustom?.value);

            const vendorBrand = node.vendor || "";
            const brandTokens = Array.from(new Set([...markaTokens, vendorBrand].filter(Boolean)));

            return {
                id: node.id,
                handle: node.handle,
                name: decodeEntities(node.title),
                categoryTokens: catTokens,
                mainCategoryTokens: mainTokens,
                subCategoryTokens: subTokens,
                brandTokens,

                _categoryCanon: catTokens.map(canon),
                _brandKeys: brandTokens.map(brandKeyset),

                isBestseller: truthy(node.bestsellerCustom?.value),
                isNewCollection: truthy(node.newcollectionCustom?.value),
                hasAnyDiscount: hasDiscount,

                imageUrl: node.images?.edges?.[0]?.node?.url ?? "/images/placeholder-image.webp",
                imageAlt: decodeEntities(
                    node.images?.edges?.[0]?.node?.altText ?? node.title
                ),

                price: {
                    price: lowest,
                    discountedPrice: lowestCompare && lowestCompare > lowest ? lowestCompare : null,
                },
                currency,

                variants: vEdges.map(({ node: v }) => ({
                    id: v.id,
                    title: decodeEntities(v.title),
                    options: v.selectedOptions || [],
                    price: {
                        price: Number(v.price.amount),
                        discountedPrice:
                            v.compareAtPrice?.amount &&
                            Number(v.compareAtPrice.amount) > Number(v.price.amount)
                                ? Number(v.compareAtPrice.amount)
                                : null,
                    },
                    currency: v.price.currencyCode,
                    availableForSale: v.availableForSale,
                    currentlyNotInStock: v.currentlyNotInStock,
                })),
            };
        });

        /* ---------------- LOCAL FILTERING ---------------- */
        const matchMainSub = (p) => {
            if (normalizeSpaces(mainCategory)) {
                if (!p.mainCategoryTokens.some((m) => equalText(m, mainCategory))) return false;
            }
            if (normalizeSpaces(subCategory)) {
                if (!p.subCategoryTokens.some((m) => equalText(m, subCategory))) return false;
            }
            return true;
        };

        const matchCategory = (p) => {
            if (!kCanonList.length) return true;
            return p._categoryCanon.some((c) => kCanonList.includes(c));
        };

        const matchBrand = (p) => {
            if (!wantBrandKeys.length) return true;

            for (const w of wantBrandKeys) {
                for (const h of p._brandKeys) {
                    if (w.full === h.full || w.compact === h.compact) return true;

                    if (
                        w.tokens.length &&
                        h.tokens.length &&
                        w.tokens.every((t) => h.tokens.includes(t))
                    )
                        return true;
                }
            }
            return false;
        };

        const matchBestseller = (p) =>
            isBestseller == null ? true : p.isBestseller === isBestseller;

        const matchNew = (p) =>
            isNewCollection == null ? true : p.isNewCollection === isNewCollection;

        const matchPromo = (p) =>
            typeof isPromocja !== "boolean"
                ? true
                : isPromocja
                    ? p.hasAnyDiscount
                    : !p.hasAnyDiscount;

        const matchPrice = (p) => {
            const rng = normalizeRange(priceRange);
            if (!rng) return true;
            const pr = p.price.price;
            if (rng.min != null && pr < rng.min) return false;
            if (rng.max != null && pr > rng.max) return false;
            return true;
        };

        const matchName = (p) => {
            if (!nameNeedle) return true;
            const base = stripAcc(p.name).toLowerCase();
            return base.includes(stripAcc(nameNeedle).toLowerCase());
        };

        /* FINAL FILTER RESULT */
        const filtered = mapped.filter(
            (p) =>
                matchMainSub(p) &&
                matchCategory(p) &&
                matchBrand(p) &&
                matchBestseller(p) &&
                matchNew(p) &&
                matchPromo(p) &&
                matchPrice(p) &&
                matchName(p)
        );

        /* ---------------- RETURN EXACTLY ONE SHOPIFY PAGE ---------------- */
        return {
            products: filtered,
            endCursor: data?.products?.pageInfo?.endCursor ?? null,
            hasNextPage: data?.products?.pageInfo?.hasNextPage ?? false,
        };
    } catch (err) {
        console.error("loadProducts fatal:", err);
        return { products: [], endCursor: null, hasNextPage: false };
    }
};



/* ============================================================
 * Vendors by product type
 * ============================================================ */
export const getVendorsByProductType = async (productType, limit = 250) => {
    const query = `
    query GetVendors($first: Int!, $query: String!) {
      products(first: $first, query: $query) {
        edges { node { vendor } }
      }
    }
  `;
    const variables = {first: limit, query: `productType:'${productType}'`};
    const data = await shopifyFetch(query, variables);
    const vendors = data.products.edges.map(edge => edge.node.vendor);
    return [...new Set(vendors)];
};

/* ============================================================
 * Title search helpers + findProductsByTitleMatch (UNIVERSAL)
 * ============================================================ */
const stripDiacritics = (str = "") =>
    str.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ł/g, "l")
        .replace(/Ł/g, "L");

// Normalize: remove diacritics, lowercase, replace non-letter/number with space, collapse spaces
const normalizeForSearch = (s = "") =>
    stripDiacritics(String(s))
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .replace(/\s+/g, " ")
        .trim();

const splitTokens = (s = "") => normalizeForSearch(s).split(" ").filter(Boolean);

// Local AND matcher: every token must appear somewhere in the normalized title
const titleMatchesAllTokens = (title, query) => {
    const normTitle = normalizeForSearch(title);
    const tokens = splitTokens(query);
    if (!tokens.length) return true;
    return tokens.every((t) => normTitle.includes(t));
};

// Build a broad, diacritic-tolerant Shopify query from tokens.
// For each token we OR original + no-diacritics (+ a 4-char prefix for long tokens) and AND groups.
// Example: (title:*podkład* OR title:*podklad* OR title:*podk*) AND (...)
const buildLooseTitleQuery = (input) => {
    const raw = String(input || "").trim();
    if (!raw) return "title:*";
    const rawTokens = raw.split(/\s+/).filter(Boolean);
    if (!rawTokens.length) return "title:*";

    const groups = rawTokens.map((tok) => {
        const base = tok.toLowerCase().replace(/[":']/g, "");
        const noDia = stripDiacritics(base);
        const variants = new Set();

        if (base.length >= 2) variants.add(base);
        if (noDia.length >= 2) variants.add(noDia);

        // Add prefixes for long tokens to catch partials (e.g., booster -> boos)
        if (base.length >= 6) variants.add(base.slice(0, 4));
        if (noDia.length >= 6) variants.add(noDia.slice(0, 4));

        const ors = Array.from(variants).map((v) => `title:*${v}*`);
        return ors.length > 1 ? `(${ors.join(" OR ")})` : ors[0];
    });

    return groups.join(" AND ");
};

// Universal, paginated, high-recall title search
export const findProductsByTitleMatch = async (title, after = null) => {
    const queryDoc = `
    query GetMatchingProducts($first: Int!, $query: String!, $after: String) {
      products(first: $first, sortKey: TITLE, query: $query, after: $after) {
        edges {
          cursor
          node {
            id
            handle
            title
            images(first: 1) { edges { node { url altText } } }
            variants(first: 1) { edges { node { price { amount currencyCode } } } }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

    const PAGE = 50;   // pull decent pages to improve recall in autocomplete
    const TARGET = 30; // enough items for your dropdown UX

    let activeQuery = buildLooseTitleQuery(title);
    let cursor = after || null;
    let hasNext = true;
    let collected = [];
    let usedFallback = false;
    let lastPageInfo = {endCursor: null, hasNextPage: false};

    try {
        while (collected.length < TARGET && hasNext) {
            const data = await shopifyFetch(queryDoc, {first: PAGE, query: activeQuery, after: cursor});
            const edges = data?.products?.edges || [];

            for (const {node} of edges) {
                if (titleMatchesAllTokens(node.title, title)) {
                    collected.push(node);
                    if (collected.length >= TARGET) break;
                }
            }

            lastPageInfo = {
                endCursor: data?.products?.pageInfo?.endCursor || null,
                hasNextPage: data?.products?.pageInfo?.hasNextPage || false
            };
            hasNext = lastPageInfo.hasNextPage;
            cursor = lastPageInfo.endCursor;

            // If nothing after the first page of token-query and this isn't a continuation, fallback to title:*
            if (!usedFallback && !after && collected.length === 0) {
                usedFallback = true;
                activeQuery = "title:*";
                cursor = null;
                hasNext = true;
            }
        }

        const products = collected.map((node) => {
            const img = node.images?.edges?.[0]?.node ?? null;
            const handle = node.handle || slugifyFallback(node.title);

            return {
                id: node.id,
                handle,
                name: node.title,
                imageUrl: img?.url || "/images/placeholder-image.webp",
                imageAlt: img?.altText ?? node.title,
                price: parseFloat(node.variants?.edges?.[0]?.node?.price?.amount || 0),
                currency: node.variants?.edges?.[0]?.node?.price?.currencyCode || "PLN",
            };
        });

        return {
            products,
            endCursor: cursor,
            hasNextPage: hasNext
        };
    } catch (err) {
        console.error("Failed to find products by title:", err);
        return {products: [], endCursor: null, hasNextPage: false};
    }
};

/* ============================================================
 * PRODUCT DETAILS — with qty fallback
 * ============================================================ */
export const fetchProductDetails = async (selector) => {
    let variables = null;
    let useHandle = false;

    if (typeof selector === "string" && selector.trim()) {
        const raw = selector.trim();
        if (raw.startsWith("gid://") || raw.includes("Product/")) {
            variables = { id: raw };
        } else {
            variables = { handle: raw };
            useHandle = true;
        }
    } else if (selector && typeof selector === "object") {
        if (selector.handle) {
            variables = { handle: selector.handle };
            useHandle = true;
        } else if (selector.id) {
            variables = { id: selector.id };
        }
    }

    if (!variables) throw new Error("Missing product identifier");

    const makeQuery = (afterCursor = null) => {
        const afterPart = afterCursor ? `, after: "${afterCursor}"` : "";
        const fields = `
      id
      handle
      title
      description
      descriptionHtml
      seo {
        title
        description
      }
      images(first: 50) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 250${afterPart}) {
        edges {
          cursor
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
            }
            availableForSale
            currentlyNotInStock
            quantityAvailable
            selectedOptions {
              name
              value
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    `;

        if (useHandle) {
            return `
        query GetProductByHandle($handle: String!) {
          productByHandle(handle: $handle) {
            ${fields}
          }
        }
      `;
        }
        return `
      query GetProductById($id: ID!) {
        node(id: $id) {
          ... on Product {
            ${fields}
          }
        }
      }
    `;
    };

    const run = async (withQty) => {
        let allVariants = [];
        let productData = null;
        let cursor = null;
        let hasNextPage = true;

        while (hasNextPage) {
            const res = await shopifyFetch(makeQuery(cursor), variables);
            const product = useHandle ? res?.productByHandle : res?.node;
            if (!product) throw new Error("Product not found");

            productData = product;

            const variantsPage = product.variants.edges || [];
            allVariants.push(...variantsPage);

            const pageInfo = product.variants.pageInfo;
            hasNextPage = pageInfo.hasNextPage;
            cursor = pageInfo.endCursor;
        }

        // Build option groups (labels lowercased)
        const optionMap = new Map();
        for (const edge of allVariants) {
            const variantId = edge.node.id;
            edge.node.selectedOptions?.forEach(({ name, value }) => {
                const label = name.toLowerCase();
                if (!optionMap.has(label)) optionMap.set(label, new Map());
                if (!optionMap.get(label).has(value)) optionMap.get(label).set(value, new Set());
                optionMap.get(label).get(value).add(variantId);
            });
        }
        const structuredVariants = Array.from(optionMap.entries()).map(([label, valuesMap]) => ({
            label,
            options: Array.from(valuesMap.entries()).map(([value, idSet]) => ({
                value,
                variantID: Array.from(idSet)[0],
            })),
        }));

        // Min price across variants
        let minPrice = Infinity, minCurrency = "PLN", minCompareAt = null;
        for (const { node: v } of allVariants) {
            const p = parseFloat(v.price.amount);
            const cp = v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : null;
            if (p < minPrice) {
                minPrice = p;
                minCurrency = v.price.currencyCode;
                minCompareAt = cp;
            }
        }
        if (!Number.isFinite(minPrice)) minPrice = 0;

        const variantList = allVariants.map(({ node }) => {
            const p = parseFloat(node.price.amount);
            const cp = node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null;
            const discounted = (cp !== null && cp > p) ? cp : null;
            return {
                id: node.id,
                title: node.title,
                price: { price: p, discountedPrice: discounted },
                currency: node.price.currencyCode,
                options: node.selectedOptions,
                quantityAvailable: withQty && typeof node.quantityAvailable === "number" ? node.quantityAvailable : null,
                availableForSale: !!node.availableForSale,
                currentlyNotInStock: !!node.currentlyNotInStock,
            };
        });

        const handle = productData.handle || slugifyFallback(productData.title);

        return {
            id: productData.id,
            handle,
            name: productData.title,
            description: productData.description,
            descriptionHtml: productData.descriptionHtml,
            seo: {
                title: productData.seo?.title || null,
                description: productData.seo?.description || null,
            },
            details: "Brak dodatkowych danych technicznych.",
            images: (productData.images?.edges || []).map(({ node }) => ({
                url: node.url,
                alt: node.altText || productData.title || "",
            })),
            variants: structuredVariants,
            variantList,
            price: {
                price: minPrice,
                discountedPrice: (minCompareAt !== null && minCompareAt > minPrice) ? minCompareAt : null,
            },
            currency: minCurrency,
            rating: { score: Math.floor(Math.random() * 5) + 1, amount: Math.floor(Math.random() * 300) + 1 },
            reviews: [],
        };
    };

    try {
        return await run(true);
    } catch (err) {
        if (lacksInventoryScope(err)) {
            console.warn("Storefront token missing inventory scope; falling back without quantityAvailable");
            return await run(false);
        }
        console.error("Failed to fetch product details:", err);
        return null;
    }
};



/* ============================================================
 * getProductVariants — normalized + fallback for qty
 * ============================================================ */
export async function getProductVariants(productId) {
    if (!productId) throw new Error("Missing productId");

    const makeQuery = (withQty) => `
    query GetProductVariants($id: ID!) {
      node(id: $id) {
        ... on Product {
          id
          options { name }
          variants(first: 100) {
            edges {
              node {
                id
                selectedOptions { name value }
                ${withQty ? "quantityAvailable" : ""}
                availableForSale
                currentlyNotInStock
              }
            }
          }
        }
      }
    }
  `;

    const run = async (withQty) => {
        const res = await shopifyFetch(makeQuery(withQty), {id: productId});
        const product = res?.node;
        if (!product) return [];
        return (product.variants?.edges || []).map(({node}) => {
            const variantData = {};
            node.selectedOptions?.forEach(opt => {
                variantData[opt.name] = opt.value;
            });
            variantData.id = node.id;
            variantData.quantityAvailable = withQty && typeof node.quantityAvailable === "number" ? node.quantityAvailable : null;
            variantData.availableForSale = !!node.availableForSale;
            variantData.currentlyNotInStock = !!node.currentlyNotInStock;
            return variantData;
        });
    };

    try {
        return await run(true);
    } catch (err) {
        if (lacksInventoryScope(err)) {
            console.warn("Missing inventory scope; getProductVariants fallback without qty");
            return await run(false);
        }
        console.error("getProductVariants error:", err);
        return [];
    }
}

/* ============================================================
 * checkStock — prefer INTEGER quantityAvailable; fallback to boolean-ish
 * ============================================================ */
export const checkStock = async (variantGid) => {
    const makeQuery = (withQty) => `
    query VariantStock($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          id
          ${withQty ? "quantityAvailable" : ""}
          availableForSale
          currentlyNotInStock
        }
      }
    }
  `;
    try {
        // try with qty
        let res = await shopifyFetch(makeQuery(true), {id: variantGid});
        const v1 = res?.node;
        if (v1 && typeof v1.quantityAvailable === "number") return v1.quantityAvailable;

        // fallback without qty
        res = await shopifyFetch(makeQuery(false), {id: variantGid});
        const v2 = res?.node;
        if (!v2) return null;
        return v2.availableForSale && !v2.currentlyNotInStock ? null : 0;
    } catch (err) {
        if (lacksInventoryScope(err)) {
            try {
                const res = await shopifyFetch(makeQuery(false), {id: variantGid});
                const v = res?.node;
                return v?.availableForSale && !v?.currentlyNotInStock ? null : 0;
            } catch (e2) {
                console.error("checkStock (fallback) error:", e2);
                return null;
            }
        }
        console.error("checkStock error:", err);
        return null;
    }
};

/* ============================================================
 * Briefs: bestsellers / new collection (custom namespace)
 * ============================================================ */
export const fetchBestsellersBrief = async () => {
    const queryDoc = `
      query GetBestsellers($first: Int!, $after: String, $query: String) {
        products(first: $first, after: $after, query: $query, sortKey: TITLE) {
          edges {
            node {
              id
              handle
              title
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              kategoriaMetafield: metafield(
                namespace: "custom"
                key: "kategoria"
              ) {
                value
              }
              bestsellerMetafield: metafield(
                namespace: "custom"
                key: "bestseller"
              ) {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const queryFilter = `metafields.custom.bestseller:true`;

    const results = [];
    let after = undefined;        // ⬅️ critical
    let lastCursor = null;
    let hasNextPage = true;
    let safetyCounter = 0;

    try {
        while (hasNextPage) {
            if (++safetyCounter > 5) {
                console.error("Pagination aborted: safety limit reached");
                break;
            }

            const variables = {
                first: 50,
                query: queryFilter,
                ...(after && { after })   // ⬅️ omit when undefined
            };

            const res = await shopifyFetch(queryDoc, variables);
            const products = res?.products;

            if (!products) break;

            for (const { node } of products.edges ?? []) {
                const isBest =
                    String(node?.bestsellerMetafield?.value ?? "")
                        .trim()
                        .toLowerCase() === "true";

                if (!isBest) continue;

                const imgNode = node?.images?.edges?.[0]?.node ?? null;

                results.push({
                    name: node.title,
                    category: node.kategoriaMetafield?.value || null,
                    price: Number(
                        node.priceRange?.minVariantPrice?.amount ?? 0
                    ),
                    image: imgNode?.url ?? null,
                    imageAlt: imgNode?.altText ?? node.title,
                    id: node.id,
                    handle: node.handle || slugifyFallback(node.title)
                });
            }

            const pageInfo = products.pageInfo;
            const nextCursor = pageInfo?.endCursor ?? null;

            if (!pageInfo?.hasNextPage) break;

            if (!nextCursor || nextCursor === lastCursor) {
                console.error("Pagination stuck: cursor did not advance");
                break;
            }

            lastCursor = nextCursor;
            after = nextCursor;
            hasNextPage = true;
        }

        // Fisher–Yates shuffle (safe)
        for (let i = results.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [results[i], results[j]] = [results[j], results[i]];
        }

        return results;
    } catch (err) {
        console.error("Failed to fetch bestsellers:", err);
        return [];
    }
};


export const fetchNewCollectionBrief = async () => {
    const queryFilter = `metafields.custom.newcollection:true`;

    const queryDoc = `
      query GetNewCollection($first: Int!, $after: String, $query: String) {
        products(first: $first, after: $after, query: $query, sortKey: TITLE) {
          edges {
            node {
              id
              handle
              title
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              newcollectionMetafield: metafield(
                namespace: "custom"
                key: "newcollection"
              ) {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const results = [];
    let after = undefined;      // IMPORTANT: undefined, not null
    let hasNextPage = true;
    let lastCursor = null;
    let safetyCounter = 0;

    try {
        while (hasNextPage) {
            if (++safetyCounter > 5) {
                console.error("Pagination aborted: safety limit reached");
                break;
            }

            const variables = {
                first: 50,
                query: queryFilter,
                ...(after && { after })
            };

            const res = await shopifyFetch(queryDoc, variables);
            const products = res?.products;

            if (!products) break;

            for (const { node } of products.edges ?? []) {
                const isNew =
                    String(node?.newcollectionMetafield?.value ?? "")
                        .trim()
                        .toLowerCase() === "true";

                if (!isNew) continue;

                const imgNode = node?.images?.edges?.[0]?.node;
                if (!imgNode?.url) continue;

                results.push({
                    id: node.id,
                    handle: node.handle || slugifyFallback(node.title),
                    url: imgNode.url,
                    alt: imgNode.altText ?? node.title ?? ""
                });
            }

            const nextCursor = products.pageInfo?.endCursor ?? null;

            if (!products.pageInfo?.hasNextPage) break;

            if (!nextCursor || nextCursor === lastCursor) {
                console.error("Pagination stuck: cursor did not advance");
                break;
            }

            lastCursor = nextCursor;
            after = nextCursor;
            hasNextPage = true;
        }

        return results;
    } catch (err) {
        console.error("Failed to fetch new collection images:", err);
        return [];
    }
};

